import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from "@angular/material/legacy-dialog";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { useSelectInput } from "@app/_helpers/html/selectInput";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { coerceSecondsFromTimeFormat, coerceSecondsToTimeFormat } from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "@app/components/user-single-picker-dialog/user-single-picker-dialog.component";
import { MatRightSideSheet } from "@app/shared/mat-right-side-sheet/mat-right-side-sheet";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { UserSettingsQuery } from "@timeghost/web-api";
import { isValid } from "date-fns/esm";
import { filter } from "rxjs";
import { CreditDeductItem } from "./credit-deduct-types";
export interface CreditDeductDialogData {
	disabledFields?: Record<string, boolean>;
	user: WorkspaceUser;
	value?: CreditDeductItem;
}
const DESCRIPTION_MAX_LENGTH = 255;
@Component({
	selector: "tg-credit-deduct-dialog",
	templateUrl: "./credit-deduct-dialog.component.html",
	styleUrl: "./credit-deduct-dialog.component.scss",
})
export class CreditDeductDialogComponent implements OnInit {
	readonly group = new FormGroup({
		description: new FormControl("", [Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
		value: new FormControl("00:00", [CustomValidators.minDuration(1000)]),
		date: new FormControl<Date>(new Date()),
		type: new FormControl<"credit" | "deduct">("credit"),
		user: new FormControl<WorkspaceUser>(null),
	});
	readonly maxLength = DESCRIPTION_MAX_LENGTH;
	constructor(
		@Inject(MAT_LEGACY_DIALOG_DATA)
		private data: CreditDeductDialogData,
		private ref: MatLegacyDialogRef<CreditDeductDialogComponent>,
		private userSettingsQuery: UserSettingsQuery,
		private dialog: MatRightSideSheet
	) {}
	get value() {
		return this.data?.value;
	}
	refreshGroup({ description, type, value, date }: typeof this.group.value) {
		this.group.patchValue({
			description,
			date,
			type,
			value: coerceSecondsToTimeFormat(value as any, false),
			user: this.data.user,
		});
		if (this.value) {
			this.group.setMustChange();
		}
	}

	async submit() {
		if (this.group.disabled || this.group.invalid) return;
		const userSettings = this.userSettingsQuery.getValue();
		const author = userSettings.workspace.users?.find((u) => u.id === userSettings.id);
		const { value, ...restOfValues } = this.group.getRawValue();
		return this.ref.close({
			...restOfValues,
			value: coerceSecondsFromTimeFormat(value),
			user: author.id,
		});
	}
	delete() {
		if (this.group.disabled) return;
		const userSettings = this.userSettingsQuery.getValue();
		const author = userSettings.workspace.users?.find((u) => u.id === userSettings.id);
		const { value, ...restOfValues } = this.group.getRawValue();
		return this.ref.close({
			...restOfValues,
			value: coerceSecondsFromTimeFormat(value),
			user: author.id,
			deleted: true,
		});
	}
	ngOnInit() {
		this.ref.updateSize("560px");
		// this.ref.addPanelClass(['mat-dialog-vanilla', 'mat-dialog-relative']);

		if (this.value) this.refreshGroup(this.value as any);
		else
			this.refreshGroup({
				description: "",
				date: new Date(),
				user: null,
				type: "credit",
				value: "00:00",
			});
		if (this.data.disabledFields) {
			Object.entries(this.data.disabledFields).forEach(([key, disabled]) => {
				if (!disabled) return;
				this.group.controls[key].disable();
			});
		}
		this.group.updateValueAndValidity();
	}

	readonly selectInput = useSelectInput();
	openUserPicker() {
		const user = this.group.getRawValue()?.user;
		if (!user || this.group.disabled || this.group.controls.user.disabled) return;
		this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
							this.userSettingsQuery.getValue()
						);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user)
					this.group.patchValue({
						user,
					});
			});
	}
	openCalPicker() {
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.group.getRawValue().date),
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.group.patchValue({
					date: x,
				});
			});
	}
}
